<template>
    <Teleport to="body">
        <div class="modal-overlay">
            <div class="modal-background" @click="emit('close')"></div>
            <div class="modal-content ha-modal">
                <template v-if="isFfd">
                    <h3>Importer une liste FFD</h3>
                    <p class="ha-hint">
                        Liste des inscrits publiée par la FFD (.pdf, une section par catégorie) : vérifiez l'aperçu.
                        Les inscriptions sont créées comme réglées auprès de la FFD (exemptées, 0 €).
                        Ré-importer une liste plus récente n'ajoute que les nouveaux inscrits.
                    </p>
                </template>
                <template v-else>
                    <h3>Importer un export HelloAsso</h3>
                    <p class="ha-hint">
                        Billetterie gérée directement sur HelloAsso : exportez la liste des participants (.xlsx) puis
                        vérifiez l'aperçu. Les inscriptions sont créées comme payées en ligne, au montant du billet.
                        Ré-importer un export plus récent n'ajoute que les nouveaux billets.
                    </p>
                </template>

                <div class="ha-file">
                    <input type="file" :accept="isFfd ? '.pdf' : '.xlsx'" @change="onFile" />
                    <button class="btn-submit" :disabled="!file || loading" @click="runPreview(false)">
                        {{ loading ? 'Analyse...' : 'Analyser' }}
                    </button>
                </div>

                <template v-if="preview">
                    <p class="ha-summary">
                        {{ preview.tickets }} {{ isFfd ? 'ligne(s)' : 'billet(s) valide(s)' }},
                        {{ preview.players.length }} joueur(s) à importer
                        <span v-if="preview.already_registered"> | {{ preview.already_registered }} déjà inscrit(s), masqué(s)</span>
                        <span v-if="preview.skipped"> | {{ preview.skipped }} commande(s) non validée(s) ignorée(s)</span>
                    </p>
                    <p v-if="doubtCount" class="ha-doubt-summary">
                        {{ doubtCount }} joueur(s) avec un doute d'orthographe : vérifiez s'il s'agit de la même
                        personne et corrigez le nom si besoin avant d'importer (sinon deux inscriptions distinctes).
                    </p>

                    <h4>{{ isFfd ? 'Sections' : 'Tarifs' }}</h4>
                    <table class="ha-table">
                        <thead>
                            <tr>
                                <th>{{ isFfd ? 'Section FFD' : 'Tarif HelloAsso' }}</th>
                                <th>{{ isFfd ? 'Lignes' : 'Billets' }}</th>
                                <th>Catégorie</th><th>Licencié</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="t in preview.tarifs" :key="t.tarif">
                                <td class="ha-left">{{ t.tarif }}</td>
                                <td>{{ t.count }}</td>
                                <td>
                                    <select v-model="mapping[t.tarif].category" class="form-input ha-select">
                                        <option :value="null">Ignorer</option>
                                        <option v-for="c in CATEGORIES" :value="c.id" :key="c.id">{{ c.label }}</option>
                                    </select>
                                </td>
                                <td>
                                    <select v-model="mapping[t.tarif].is_licensed" class="form-input ha-select">
                                        <option :value="true">Oui</option>
                                        <option :value="false">Non</option>
                                        <option :value="null">?</option>
                                    </select>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <button class="btn-secondary" :disabled="loading" @click="reanalyse">
                        {{ loading ? 'Analyse...' : 'Réanalyser avec mes corrections' }}
                    </button>

                    <div class="ha-players-header">
                        <h4>Joueurs</h4>
                        <div class="ha-toggles">
                            <label class="ha-toggle">
                                <input v-model="onlyDoubts" type="checkbox" /> Seulement les doutes d'orthographe
                                ({{ doubtCount }})
                            </label>
                            <label class="ha-toggle">
                                <input v-model="onlyWarnings" type="checkbox" /> Seulement ceux avec une alerte
                                ({{ rows.filter(r => r.warnings.length || r.doubts.length).length }})
                            </label>
                        </div>
                    </div>
                    <div class="ha-table-wrap">
                        <table class="ha-table">
                            <thead>
                                <tr><th></th><th>Nom</th><th>Prénom</th><th>Club</th><th>Indiv.</th><th>Double / partenaire</th></tr>
                            </thead>
                            <tbody>
                                <template v-for="row in visibleRows" :key="row.key">
                                    <tr :class="{ 'ha-excluded': !row.include }">
                                        <td><input v-model="row.include" type="checkbox" /></td>
                                        <td><input v-model="row.name" class="form-input ha-input" /></td>
                                        <td><input v-model="row.firstname" class="form-input ha-input" /></td>
                                        <td class="ha-left ha-club">
                                            <!-- Liste FFD : sans licence connue chez nous, on ne sait pas (autre ligue ou vrai NL). -->
                                            {{ row.is_licensed ? (row.club || 'Licencié') : (isFfd ? '' : 'NL') }}
                                        </td>
                                        <td>
                                            <span v-for="s in row.simple" class="ha-chip" :class="{ 'ha-chip-done': row.already_simple.includes(s.category) }"
                                                :title="row.already_simple.includes(s.category) ? 'Déjà inscrit' : ''"
                                                :key="s.category">
                                                {{ s.category }}
                                            </span>
                                        </td>
                                        <td class="ha-left">
                                            <div v-for="d in row.doubles" class="ha-double" :key="d.category">
                                                <span class="ha-chip"
                                                    :class="{ 'ha-chip-done': row.already_double.includes(d.category) }">
                                                    {{ d.category }}
                                                </span>
                                                <input v-model="d.partner_name" class="form-input ha-input" placeholder="Nom" />
                                                <input v-model="d.partner_firstname" class="form-input ha-input" placeholder="Prénom" />
                                                <span v-if="d.partner_raw" class="ha-raw" :title="isFfd ? 'Liste FFD' : 'Saisi sur HelloAsso'">« {{ d.partner_raw }} »</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="row.doubts.length" class="ha-warn-row" :class="{ 'ha-excluded': !row.include }">
                                        <td></td>
                                        <td colspan="5" class="ha-left">
                                            <div v-for="d in row.doubts" class="ha-doubt" :key="d">
                                                <strong>Doute d'orthographe :</strong> {{ d }}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="row.warnings.length" class="ha-warn-row" :class="{ 'ha-excluded': !row.include }">
                                        <td></td>
                                        <td colspan="5" class="ha-left">
                                            <span v-for="w in row.warnings" class="ha-warn" :key="w">{{ w }}</span>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </template>

                <div class="form-actions">
                    <button v-if="preview" class="btn-submit" :disabled="committing || !includedCount" @click="commit">
                        {{ committing ? 'Import...' : `Importer ${includedCount} joueur(s)` }}
                    </button>
                    <button class="btn-secondary" @click="emit('close')">Fermer</button>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { toast } from 'vue3-toastify'
import backendApi from '@/axios/backendApi'
import { useAuthStore } from '@/stores/useAuthStore'
import { handleError } from '@/functions/utils'

// Cf. Dartshub inscriptions/helloasso_import.py : un export HelloAsso = une ligne par
// billet ; le backend regroupe par joueur et rapproche les partenaires saisis en texte
// libre, l'organisateur corrige ici avant d'importer.
// source "ffd" : liste PDF des inscrits de la FFD (cf. Dartshub inscriptions/ffd_import.py),
// convertie côté backend au même format (une section = un tarif), même aperçu.

const props = withDefaults(defineProps<{ eventId: number; source?: 'helloasso' | 'ffd' }>(), { source: 'helloasso' })
const isFfd = computed(() => props.source === 'ffd')
const emit = defineEmits<{ close: []; imported: [] }>()

const authStore = useAuthStore()

const CATEGORIES = [
    { id: 'M', label: 'M (Masculin)' },
    { id: 'F', label: 'F (Féminin)' },
    { id: 'V', label: 'V (Vétéran)' },
    { id: 'J', label: 'J (Junior)' },
    { id: 'DM', label: 'DM (Double)' },
    { id: 'DF', label: 'DF (Double féminin)' },
]

type Partner = { name: string; firstname: string; is_licensed: boolean | null }
type PreviewPlayer = {
    key: string
    name: string
    firstname: string
    club: string | null
    is_licensed: boolean
    simple: { category: string; amount_cents: number }[]
    doubles: { category: string; amount_cents: number; partner_raw: string; partner: Partner | null; lines: number[] }[]
    lines: number[]
    already_simple: string[]
    already_double: string[]
    payer_email: string
    payer_name: string
    warnings: string[]
    // Même personne sous deux orthographes ? (cf. Dartshub helloasso_import.Directory.similar)
    doubts: string[]
}
type Preview = {
    tickets: number
    skipped: number
    already_registered: number
    tarifs: { tarif: string; count: number; category: string | null; is_licensed: boolean | null }[]
    players: PreviewPlayer[]
}
type Row = Omit<PreviewPlayer, 'doubles'> & {
    include: boolean
    orig_name: string
    orig_firstname: string
    doubles: (PreviewPlayer['doubles'][number] & {
        partner_name: string; partner_firstname: string; orig_partner_name: string; orig_partner_firstname: string
    })[]
}
// Corrections cumulées par numéro de ligne du fichier (cf. Dartshub
// helloasso_import.apply_corrections) : chaque réanalyse repart du fichier d'origine.
type Correction = { name?: string; firstname?: string; partner_name?: string; partner_firstname?: string }
type Mapping = Record<string, { category: string | null; is_licensed: boolean | null }>

const file = ref<File | null>(null)
const loading = ref(false)
const committing = ref(false)
const preview = ref<Preview | null>(null)
const rows = ref<Row[]>([])
const mapping = ref<Mapping>({})
const onlyWarnings = ref(false)
const onlyDoubts = ref(false)
const corrections = ref<Record<string, Correction>>({})
const includeByLine = ref<Record<number, boolean>>({})
const doubtCount = computed(() => rows.value.filter(r => r.doubts.length).length)
const visibleRows = computed(() => rows.value.filter(r =>
    (!onlyDoubts.value || r.doubts.length) && (!onlyWarnings.value || r.warnings.length || r.doubts.length)))
const includedCount = computed(() => rows.value.filter(r => r.include).length)

const onFile = (e: Event) => {
    file.value = (e.target as HTMLInputElement).files?.[0] ?? null
    preview.value = null
    rows.value = []
    corrections.value = {}
    includeByLine.value = {}
}

const reanalyse = () => {
    for (const r of rows.value) {
        for (const line of r.lines) includeByLine.value[line] = r.include
        if (r.name.trim() !== r.orig_name || r.firstname.trim() !== r.orig_firstname) {
            for (const line of r.lines) {
                corrections.value[line] = { ...corrections.value[line], name: r.name.trim(), firstname: r.firstname.trim() }
            }
        }
        for (const d of r.doubles) {
            if (d.partner_name.trim() === d.orig_partner_name && d.partner_firstname.trim() === d.orig_partner_firstname) continue
            for (const line of d.lines) {
                corrections.value[line] = {
                    ...corrections.value[line],
                    partner_name: d.partner_name.trim(),
                    partner_firstname: d.partner_firstname.trim(),
                }
            }
        }
    }
    runPreview(true)
}

const runPreview = async (withMapping: boolean) => {
    if (!file.value) return
    loading.value = true
    try {
        const form = new FormData()
        form.append('file', file.value)
        if (withMapping) {
            form.append('mapping', JSON.stringify(mapping.value))
            form.append('corrections', JSON.stringify(corrections.value))
        }
        const { data } = await backendApi.post<Preview>(
            `/inscriptions/helloasso-import/preview?event_id=${props.eventId}&source=${props.source}`, form,
            { headers: { Authorization: `Bearer ${authStore.token}` }, timeout: 60000 },
        )
        preview.value = data
        mapping.value = Object.fromEntries(
            data.tarifs.map(t => [t.tarif, { category: t.category, is_licensed: t.is_licensed }]),
        )
        rows.value = data.players.map(p => {
            const chosen = p.lines.map(l => includeByLine.value[l]).filter(v => v !== undefined)
            return {
                ...p,
                orig_name: p.name,
                orig_firstname: p.firstname,
                // Choix fait avant la réanalyse, sinon : rien de nouveau à importer (déjà
                // inscrit partout) = décoché d'office.
                include: chosen.length ? chosen.some(Boolean)
                    : p.simple.some(s => !p.already_simple.includes(s.category))
                        || p.doubles.some(d => !p.already_double.includes(d.category)),
                doubles: p.doubles.map(d => ({
                    ...d,
                    partner_name: d.partner?.name ?? '',
                    partner_firstname: d.partner?.firstname ?? '',
                    orig_partner_name: d.partner?.name ?? '',
                    orig_partner_firstname: d.partner?.firstname ?? '',
                })),
            }
        })
    } catch (err) {
        handleError(err, `previewing ${props.source} import`)
    } finally {
        loading.value = false
    }
}

const commit = async () => {
    const players = rows.value.filter(r => r.include)
    const invalid = players.find(r => !r.name.trim() || !r.firstname.trim()
        || r.doubles.some(d => !!d.partner_name.trim() !== !!d.partner_firstname.trim()))
    if (invalid) {
        toast.error(`${invalid.firstname} ${invalid.name} : nom/prénom (ou ceux du partenaire) incomplets.`)
        return
    }
    committing.value = true
    try {
        const { data } = await backendApi.post('/inscriptions/helloasso-import/commit', {
            event_id: props.eventId,
            source: props.source,
            players: players.map(r => ({
                name: r.name.trim(),
                firstname: r.firstname.trim(),
                is_licensed: r.is_licensed,
                simple: r.simple,
                doubles: r.doubles.map(d => {
                    const original = d.partner
                    const name = d.partner_name.trim()
                    const firstname = d.partner_firstname.trim()
                    const unchanged = original && original.name === name && original.firstname === firstname
                    return {
                        category: d.category,
                        amount_cents: d.amount_cents,
                        partner: name && firstname
                            ? { name, firstname, is_licensed: unchanged ? original.is_licensed : null }
                            : null,
                    }
                }),
                payer_email: r.payer_email,
                payer_name: r.payer_name,
            })),
        }, { headers: { Authorization: `Bearer ${authStore.token}` }, timeout: 60000 })
        toast.success(`${data.created} inscription(s) créée(s), ${data.updated} complétée(s).`)
        emit('imported')
    } catch (err) {
        handleError(err, `committing ${props.source} import`)
    } finally {
        committing.value = false
    }
}
</script>

<style scoped>
/* Boutons de EventInscriptions.vue, scopés là-bas : la modale est téléportée. */
.form-actions {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-top: 1rem;
}

.btn-submit {
    padding: 0.6rem 1.4rem;
    background: var(--color-accent, #1a3a5c);
    color: var(--color-bg, #fff);
    border: none;
    border-radius: var(--radius, 8px);
    cursor: pointer;
    font-size: 0.95rem;
    font-weight: 600;
}

.btn-submit:disabled,
.btn-secondary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-secondary {
    padding: 0.6rem 1.2rem;
    background: transparent;
    border: 1px solid var(--color-light-shadow);
    color: var(--color-fg, #333);
    border-radius: var(--radius, 8px);
    cursor: pointer;
    font-size: 0.9rem;
}

.btn-secondary:hover:not(:disabled) {
    border-color: var(--color-accent);
}

.ha-modal {
    max-width: 1100px;
    width: 95%;
    text-align: left;
}

.ha-hint {
    font-size: 0.9rem;
    color: var(--color-fg-darker);
}

.ha-file {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex-wrap: wrap;
    margin: 1rem 0;
}

.ha-summary {
    font-weight: 600;
}

/* Pas de scroll vertical propre : la modale (max-height 90vh) défile déjà. */
.ha-table-wrap {
    overflow-x: auto;
}

.ha-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
}

.ha-table th,
.ha-table td {
    padding: 0.3rem 0.4rem;
    border-bottom: 1px solid var(--color-light-shadow);
    text-align: center;
    vertical-align: middle;
}

.ha-table .ha-left {
    text-align: left;
}

.ha-select {
    padding: 0.2rem;
    font-size: 0.85rem;
}

.ha-input {
    padding: 0.2rem 0.35rem;
    font-size: 0.85rem;
    width: 8rem;
}

.ha-club {
    font-size: 0.8rem;
    max-width: 10rem;
}

.ha-chip {
    display: inline-block;
    padding: 0.1rem 0.4rem;
    margin: 0 0.15rem;
    border-radius: var(--radius, 8px);
    background: var(--color-accent);
    color: var(--color-bg);
    font-weight: 600;
    font-size: 0.75rem;
}

.ha-chip-done {
    background: var(--color-light-shadow);
    color: var(--color-fg);
    text-decoration: line-through;
}

.ha-double {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    flex-wrap: wrap;
}

.ha-raw {
    font-size: 0.75rem;
    color: var(--color-fg-darker);
}

.ha-warn-row td {
    border-bottom: 1px solid var(--color-light-shadow);
    padding-top: 0;
}

.ha-warn {
    display: inline-block;
    margin-right: 0.75rem;
    font-size: 0.75rem;
    color: var(--color-error, #e74c3c);
}

.ha-excluded {
    opacity: 0.45;
}

.ha-doubt-summary {
    padding: 0.5rem 0.75rem;
    border-left: 4px solid var(--color-warning, #e67e22);
    background: color-mix(in srgb, var(--color-warning, #e67e22) 12%, transparent);
    border-radius: var(--radius, 8px);
    font-size: 0.9rem;
}

.ha-doubt {
    font-size: 0.8rem;
    color: var(--color-warning, #e67e22);
    padding: 0.1rem 0;
}

.ha-toggles {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
}

.ha-players-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.ha-toggle {
    font-size: 0.85rem;
    display: flex;
    gap: 0.3rem;
    align-items: center;
}
</style>
